import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { DEFAULT_THEME_ID, resolveTheme, listThemes } from '../themes/index.js'

/**
 * Theme runtime: which visual package is active, and light vs dark within it.
 *
 * Two independent axes, deliberately kept separate:
 *   - **theme pack** (`data-theme-pack` on <html>) — the whole design language.
 *     Every theme's stylesheet scopes itself to its own pack id, so switching
 *     is a single attribute write with no async load and no flash.
 *   - **colour mode** (`data-theme` on <html>) — light / dark / follow-system,
 *     resolved inside whichever pack is active.
 *
 * Layer rule: this file decides *which* stylesheet applies, never what is in
 * one. No colour value appears here.
 *
 * 主题运行时：当前启用哪个视觉包，以及包内的明暗模式。
 *
 * 两个刻意分开的独立维度：
 *   - **主题包**（<html> 上的 data-theme-pack）——整套设计语言。每个主题的样式表
 *     都把自己限定在自己的包 id 下，因此切换只是写一个属性，无异步加载、无闪烁。
 *   - **明暗模式**（<html> 上的 data-theme）——浅色 / 深色 / 跟随系统，
 *     在当前主题包内解析。
 *
 * 分层规则：本文件只决定「哪张样式表生效」，绝不决定样式表里写什么。
 * 这里不出现任何颜色值。
 */

const THEME_STORAGE_KEY = 'aacvr.themePack'
const MODE_STORAGE_KEY = 'aacvr.theme'

/** Colour-mode options. 明暗模式选项。 */
export const COLOR_MODES = ['system', 'light', 'dark']

const ThemeContext = createContext(null)

/**
 * Read a stored value, tolerating environments where localStorage throws.
 *
 * 读取已存值，容忍 localStorage 抛错的环境。
 *
 * @param {string} key Storage key / 存储键。
 * @param {string[]} allowed Allowed values / 允许的取值。
 * @param {string} fallback Value when absent or invalid / 缺失或非法时的取值。
 * @returns {string} Resolved value / 解析结果。
 */
function readStored(key, allowed, fallback) {
  try {
    const stored = localStorage.getItem(key)
    if (stored && allowed.includes(stored)) return stored
  } catch {
    // Private browsing / disabled storage — use the fallback.
    // 隐私模式或存储被禁用——用回退值。
  }
  return fallback
}

/**
 * Provide theme state to the tree.
 *
 * 向组件树提供主题状态。
 *
 * @param {{children: React.ReactNode}} props Children / 子节点。
 * @returns {JSX.Element} Provider / 上下文提供者。
 */
export function ThemeProvider({ children }) {
  const themeIds = useMemo(() => listThemes().map((theme) => theme.id), [])
  const [themeId, setThemeId] = useState(() => readStored(THEME_STORAGE_KEY, themeIds, DEFAULT_THEME_ID))
  const [mode, setMode] = useState(() => readStored(MODE_STORAGE_KEY, COLOR_MODES, 'system'))
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
  )

  // Track the OS preference so 'system' stays live rather than sampled once.
  // 跟踪系统偏好，让 'system' 模式保持实时，而不是只在启动时采样一次。
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event) => setSystemDark(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme-pack', themeId)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeId)
    } catch {
      // Non-fatal: the choice just will not survive a reload.
      // 非致命：只是刷新后不保留选择。
    }
  }, [themeId])

  useEffect(() => {
    // 'system' removes the attribute entirely so the stylesheet's
    // prefers-color-scheme media query takes over — that is what lets a theme
    // define its dark palette declaratively.
    // 'system' 直接移除属性，交给样式表里的 prefers-color-scheme 媒体查询接管——
    // 主题正是靠这一点声明式地定义深色配色。
    if (mode === 'system') document.documentElement.removeAttribute('data-theme')
    else document.documentElement.setAttribute('data-theme', mode)
    try {
      localStorage.setItem(MODE_STORAGE_KEY, mode)
    } catch {
      // Non-fatal, as above. 同上，非致命。
    }
  }, [mode])

  const isDark = mode === 'dark' || (mode === 'system' && systemDark)

  const value = useMemo(
    () => ({
      themeId,
      theme: resolveTheme(themeId),
      themes: listThemes(),
      setThemeId,
      mode,
      setMode,
      isDark,
      /** Cycle light → dark → system. 在 浅色 → 深色 → 跟随系统 之间循环。 */
      cycleMode: () => setMode((current) => COLOR_MODES[(COLOR_MODES.indexOf(current) + 1) % COLOR_MODES.length]),
    }),
    [themeId, mode, isDark]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * Access theme state.
 *
 * 获取主题状态。
 *
 * @returns {object} Theme helpers / 主题工具集。
 */
export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>')
  return context
}

/**
 * Toggle the colour mode without pulling in the whole context shape.
 *
 * 只切换明暗模式，不必引入整个上下文结构。
 *
 * @returns {() => void} Toggle function / 切换函数。
 */
export function useColorModeToggle() {
  const { setMode, isDark } = useTheme()
  return useCallback(() => setMode(isDark ? 'light' : 'dark'), [setMode, isDark])
}
